import { useLocation, useNavigate, useParams } from "react-router-dom";
import { applyCampaign } from "../../services/api/campaign";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ApplicationStatus } from "../../types/sponsorships/campaigns";
import CampaignImageSlider from "../../components/campaign/CampaignImageSlider";
import { arrayOfUniqueStrings, getUserMobileOS, sanitizeURLs, textToTitleCase, toValidNumber } from "../../utils/helpers";
import StrategyIcon from "../../components/icons/StrategyIcon";
import DOMPurify from 'dompurify';
import { useSelector } from "react-redux";
import { AppDispatch, RootState, store } from "../../redux-stores/store";
import { useDispatch } from "react-redux";
import { setChargePerReelModal, setHeader, setProFeaturesModalVisibility, setShowFooterNav, toggleAddSocialAccountModal, toggleLocationUpdateModal, toggleProfileUpdateModal } from "../../redux-stores/slices/layoutSlice";
import { toast } from "react-toastify";
import { useConfirmation } from "../../contexts/ConfirmationContext";
import usePageTitle from "../../hooks/usePageTitle";
import InfluencerActionCardModal from "../../components/influencer/InfluencerActionCardModal";
import CampaignDetailSkeleton from "../../skeletons/CampaignDetailSkeleton";
import { fetchApplication, fetchCampaign } from "../../redux-stores/slices/campaignDetailsSlice";
import { markExpired as markCollaborationsExpired } from "../../redux-stores/slices/collaborationSlice";
import { ERROR } from "../../constants/errors";
import { POSTAPIError } from "../../types";
import InsufficientPermissionsCTAModal from "../../components/CTA/InsufficientPermissionsCTAModal";
import GenericErrorsCTAModal from "../../components/CTA/GenericErrorsCTAModal";
import CampaignApplyConfirmationModal from "../../components/campaign/CampaignApplyConfirmationModal";
import CampaignApplicationNegotiation from "../../components/campaign/CampaignApplicationNegotiation";
import CampaignSocialPostSelectionModal from "../../components/campaign/CampaignSocialPostSelectionModal";
import MilestoneMediaPNG from "../../assets/images/milestone-media.png"
import { GENERAL_GIGTYPE_ID, platforms } from "../../constants/constants";
import { getLocalStorageItem, setLocalStorageItem } from "../../utils/localStorage";
import { useLocationAccess } from "../../contexts/LocationContext";
import { Link } from "react-router-dom";
import { detectBrowser } from "../../utils/detectBrowser";
import { debug } from "../../services/api/auth";
import { SocialAccountData } from "../../types/influencer/influencerTypes";
import CampaignAccountSelectorModal from "../../components/campaign/CampaignAccountSelectorModal";
import { analyticsLog } from "../../lib/analytics";
import { isNativePlatform } from "../../utils/platformUtil";
import OutOfCredits from "../../components/campaign/OutOfCredits";
import { getNotificationPermissionStatus, showNotificationEnablePrompt } from "../../lib/pushNotifications";
import "./style.scss"
import withSlideUpModal from "../../utils/hoc/withSlideUpModalComponent";
import { defaultAppHeaderProps } from "../../types/layout/layoutTypes";
import ShareIcon from "../../components/icons/ShareIcon";

import instagramLogo from "../../assets/icons/instagram-logo.svg";
import youtubeLogo from "../../assets/icons/youtube-logo.svg";
import GetProSubscriptionCard from "../../components/cards/GetProSubscriptionCard/GetProSubscriptionCard";
import ProFeaturesModal from "../../components/modals/ProFeaturesModal/ProFeaturesModal";
import MarqueeTicker from "../../components/Ticker/MarqueeTicker";
import UpgradeToProModal from "../../components/modals/UpdgradeToProModal/UpgradeToProModal";

const ShowMoreContent: React.FC<{ showMore: { label: string, values: string[] } | null }> = ({ showMore }) => {
    return (
        <div className="pb-5">
            <div className="show-more-header py-3">
                <span className="text-dark fs-24 fw-700">{showMore?.label}</span>
            </div>

            <div
                className="show-more-content d-flex flex-column gap-2 overflow-auto"
                style={{ maxHeight: "250px" }}
            >
                {showMore?.values.map((value, index) => (
                    <div key={index} className="show-more-item">
                        <span className="text-dark fs-16 fw-400">{value}</span>
                    </div>
                ))}
            </div>
        </div>
    )
}

export const ShowMoreContentModal = withSlideUpModal(ShowMoreContent, "", false);

export const CampaignSpecsRow: React.FC<{ col1: React.ReactNode, col2: React.ReactNode }> = ({ col1, col2 }) => {
    return (
        <div className="d-flex justify-content-between">
            <div className="d-flex justify-content-start gap-2 w-100 p-075">
                {col1}
            </div>
            <span>
                <svg xmlns="http://www.w3.org/2000/svg" width="1" height="60" viewBox="0 0 1 60" fill="none">
                    <path d="M0.5 0L0.500003 59" stroke="url(#paint0_linear_1298_10881)" stroke-linecap="round" />
                    <defs>
                        <linearGradient id="paint0_linear_1298_10881" x1="-0.5" y1="4.37114e-08" x2="-0.499997" y2="59" gradientUnits="userSpaceOnUse">
                            <stop stopColor="white" stopOpacity="0" />
                            <stop offset="0.25473" stopColor="#E5E5E5" />
                            <stop offset="0.751993" stopColor="#E5E5E5" />
                            <stop offset="1" stopColor="white" stopOpacity="0" />
                        </linearGradient>
                    </defs>
                </svg>
            </span>
            <div className="d-flex justify-content-start gap-2 w-100 p-075">
                {col2}
            </div>
        </div>
    )
}

const Campaign: React.FC = () => {
    /* Page parameters */
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    /* Page parameters */

    /* Local States */
    const [applicationStatus, setApplicationStatus] = useState<ApplicationStatus>({ status: 'NOT_APPLIED' })
    const [canApply, setCanApply] = useState<boolean>(false)
    const [connectionRequiredModalVisibility, setConnectionRequiredModalVisibility] = useState<boolean>(false);
    const [applicationError, setApplicationError] = useState<null | POSTAPIError>(null)
    const [confirmationBox, setConfirmationBox] = useState(false);
    const [negotiationVisible, setNegotiationVisible] = useState(false);
    const [pendingApplication, setPendingApplication] = useState<{ languageId?: string, answers?: Record<string, string | number> } | null>(null);
    const [socialPostSelectorVisible, setSocialPostSelectorVisible] = useState(false);
    const [selectedSocialPostId, setSelectedSocialPostId] = useState<string | null>(null);
    const [showMore, setShowMore] = useState<{ label: string, values: string[] } | null>(null);
    /* Local States */

    /* Redux States */
    const campaign = useSelector((state: RootState) => state.campaignDetails.campaigns[campaignId!] ?? null);
    const campaignData = campaign ? campaign.campaignData : null;
    const applicationData = campaign ? campaign.applicationData : null
    // const profileCompletion = useSelector((state: RootState) => state.influencer.influencer?.profile_completion);
    const profileUpdateModalVisibility = useSelector((state: RootState) => state.layout.profile_update_modal_visibility)
    const locationUpdateModalVisibility = useSelector((state: RootState) => state.layout.location_update_modal_visibility)
    const addSocialAccountModalVisibility = useSelector((state: RootState) => state.layout.add_social_account_modal_visibility)
    const connectionRequired = useSelector((state: RootState) => state.influencer.connectionRequired);
    const languages = useSelector((state: RootState) => state.auth.master?.language_list) ?? {};
    const influencer = useSelector((state: RootState) => state.influencer?.influencer);
    const socialAccounts = useSelector((state: RootState) => state.influencer?.social_accounts);
    const [locationRequired, setLocationRequired] = useState<boolean>(false);
    const [socialAccount, setSocialAccount] = useState<SocialAccountData | null>(null);
    const [eligibleSocialAccounts, setEligibleSocialAccounts] = useState<SocialAccountData[]>([]);
    const [campaignPlatform, setCampaignPlatform] = useState<{ id: string, name: string } | null>(null);
    const [campaignAccountSelectorVisible, setCampaignAccountSelectorVisible] = useState<boolean>(false);
    const profileCompletion = useSelector((state: RootState) => state.influencer.influencer?.profile_completion);
    const proFeaturesModalVisibility = useSelector((state: RootState) => state.layout.pro_features_modal_visibility);
    /* Redux States */

    /* Hooks */
    const dispatch = useDispatch<AppDispatch>();
    // const confirmationBox = useConfirmation();
    usePageTitle(campaignData?.campaign?.name ?? '')
    // const { location, requestLocation, declined: locationDeclined, error: locationError, allowLocationAccess, isLocationServiceEnabled, isFetchingLocation } = useLocationAccess();
    /* Hooks */


    /* Data handling functions */
    const fetchCampaignData = useCallback(async () => {
        if (!campaignId) return;
        dispatch(fetchCampaign(campaignId));
    }, [campaignId, dispatch])

    const campaignInvitation: { invited: boolean, socialAccount: SocialAccountData | null } = useMemo(() => {
        if (!applicationData?.is_invited || !applicationData?.social_account?.uuid) return { invited: false, socialAccount: null };
        const socialAccount = socialAccounts?.find(account => account.social_account.uuid === applicationData?.social_account?.uuid);
        if (!socialAccount) return { invited: false, socialAccount: null };
        return { invited: true, socialAccount: socialAccount };
    }, [socialAccounts, applicationData])

    // The first milestone's payout seeds the suggested amount in the Make an Offer modal.
    const negotiationMilestone = useMemo(() => {
        const [firstMilestone] = [...(campaignData?.milestones ?? [])].sort((a, b) => a.sort_order - b.sort_order);
        if (!firstMilestone) return null;
        return firstMilestone;
    }, [campaignData])



    const getCriteriaMatchComponent = (skip = false) => {
        if (!applicationData?.match || skip) return "Are you sure to proceed with the application?"
        return (
            <div>
                <div className="text-center">
                    <span className="fs-3 text-uppercase pop fw-bolder">{applicationData?.match?.requirement_match}%</span>
                    <span className="fs-5 ms-2">Requirements Matched!</span>
                </div>

                <div className="mt-2">
                    {
                        applicationData?.match?.requirement_matches &&
                        Object.values(applicationData.match.requirement_matches).map((requirement, i) => {
                            return (
                                <div key={i} className="d-flex justify-content-between align-items-center mt-1">
                                    <span>{requirement.name}</span>
                                    {
                                        requirement.match ? (
                                            <span><i className="fa-solid fa-circle-check text-success fs-5"></i></span>
                                        ) : (
                                            <span><i className="fa-solid fa-circle-xmark text-danger fs-5"></i></span>
                                        )
                                    }
                                </div>
                            )
                        })
                    }
                </div>
                <div className="mt-2 text-danger">
                    Disclaimer: Application selection is decided by brand.
                </div>
            </div>
        )
    }

    const openApplyConfirmation = () => {
        setConfirmationBox(true);
    };

    const openPostApplyStep = () => {
        if (applicationData?.attach_social_post) {
            if (selectedSocialPostId) {
                openApplyConfirmation();
                return;
            }
            setSocialPostSelectorVisible(true);
            return;
        }
        openApplyConfirmation();
    };

    const showConfirmationBox = () => {
        if (!campaignData?.campaign) return;
        if (!influencer?.name || !influencer?.age || !influencer.gender || !influencer.location) { // instead of profileCompletion !== 100
            setConfirmationBox(false);
            setSocialPostSelectorVisible(false);
            return dispatch(toggleProfileUpdateModal(true));
        }
        // if(locationRequired && !influencer?.location) {
        //     setConfirmationBox(false);
        //     return dispatch(toggleLocationUpdateModal(true));
        // } 

        if (!socialAccount && eligibleSocialAccounts.length > 1) {
            return setCampaignAccountSelectorVisible(true);
        }

        if (!socialAccount)
            return dispatch(toggleAddSocialAccountModal({ active: true, ...(campaignPlatform ? { platforms: [campaignPlatform] } : {}) }));

        if (socialAccount && campaign?.campaignData?.campaign?.platform === 'ig' && campaign?.campaignData?.campaign?.connection_required && socialAccount?.status !== 'connected')
            return setConnectionRequiredModalVisibility(true);

        openPostApplyStep();
        return;
    }

    // Function to fetch user location, returns {latitude, longitude} or false/null if denied
    const fetchUserLocation = (): Promise<{ lat: number; lng: number } | false | null> => {
        return new Promise((resolve) => {
            if (!navigator.geolocation) {
                resolve(null);
                return;
            }
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    });
                },
                (error) => {
                    if (error.code === error.PERMISSION_DENIED) {
                        resolve(false);
                    } else {
                        resolve(null);
                    }
                }
            );
        });
    };

    // useEffect(() => {
    //     requestLocation?.();
    // }, [requestLocation])


    const apply = async (languageId?: string, answers?: Record<string, string | number>, quotedOffer?: string | number) => {
        if (!campaignData?.campaign || !campaignId) return;


        if (!influencer?.name || !influencer?.age || !influencer.gender || !influencer.location) { // instead of profileCompletion !== 100
            setConfirmationBox(false);
            setNegotiationVisible(false);
            return dispatch(toggleProfileUpdateModal(true));
        }


        // if(locationRequired && !influencer?.location) {
        //     setConfirmationBox(false);
        //     return dispatch(toggleLocationUpdateModal(true));
        // } 
        if (!socialAccount)
            return dispatch(toggleAddSocialAccountModal({ active: true, ...(campaignPlatform ? { platforms: [campaignPlatform] } : {}) }));

        try {

            // if (!location && locationRequired) {
            //     await debug({ location, locationDeclined, isLocationServiceEnabled, locationError, mobileOS: getUserMobileOS(), browser: detectBrowser() })
            //     toast.error("Location access is required to apply!");
            //     return false;
            // }

            // const locationCoords = location ? { lat: location.latitude, lng: location.longitude } : {};
            const locationCoords = {};

            setApplicationStatus({ status: 'PENDING' });
            const campaignReferrals = getLocalStorageItem('campaign_referrals');
            let referrer;
            if (campaignReferrals && campaignReferrals[campaignId]) {
                referrer = campaignReferrals[campaignId];
            }
            const applyResponse = await applyCampaign(campaignData?.campaign.uuid, socialAccount?.social_account?.uuid, locationCoords, languageId, referrer, answers, selectedSocialPostId ?? undefined, quotedOffer);
            analyticsLog('campaign_application_success',
                {
                    campaign_id: campaignData?.campaign.uuid,
                    social_platform: socialAccount?.social_account?.platform,
                    social_handle: socialAccount?.social_account?.handle,
                    ...(languageId ? { language: languages[languageId] } : {}),
                    campaign_name: campaignData?.campaign?.name,
                    brand_name: campaignData?.campaign?.brand?.name
                }
            )
            dispatch(fetchApplication(campaignId!))
            dispatch(markCollaborationsExpired())
            setConfirmationBox(false);
            setNegotiationVisible(false);
            setPendingApplication(null);
            setSocialPostSelectorVisible(false);
            setSelectedSocialPostId(null);
            toast.success("Applied Successfully!");
            delete campaignReferrals[campaignId];
            setLocalStorageItem('campaign_referrals', campaignReferrals);

            const promptSocialAccount = applyResponse?.social_account;
            if (promptSocialAccount?.prompt_charge_per_reel) {
                const matchedAccount = socialAccounts?.find((account) => account.social_account.uuid === promptSocialAccount.uuid);

                if (matchedAccount) {
                    setTimeout(() => {
                        dispatch(setChargePerReelModal({
                            active: true,
                            socialAccount: {
                                uuid: matchedAccount.social_account.uuid,
                                handle: matchedAccount.social_account.handle,
                                profile_picture_url: matchedAccount.social_account.profile_picture_url,
                            },
                            prefill: promptSocialAccount.charge_per_reel ?? undefined,
                        }));
                    }, 1500)
                }
            }
        } catch (error: any) {
            analyticsLog('campaign_application_failed',
                {
                    campaign_id: campaignData?.campaign.uuid,
                    social_platform: socialAccount?.social_account?.platform,
                    social_handle: socialAccount?.social_account?.handle,
                    ...(languageId ? { language: languages[languageId] } : {}),
                    campaign_name: campaignData?.campaign?.name,
                    brand_name: campaignData?.campaign?.brand?.name,
                    error_code: (error?.response?.data as POSTAPIError)?.error_code,
                }
            )
            setNegotiationVisible(false);
            setPendingApplication(null);
            setApplicationError(error?.response?.data)
            setApplicationStatus({ status: 'FAILED' });
        } finally {
            setConfirmationBox(false);
        }

    }
    /* Data handling functions */

    /* Helper functions */
    const getCampaignImages = () => {
        let images: string[] = campaignData?.campaign.hero_img_url ? [campaignData.campaign.hero_img_url] : [];
        if (campaignData?.campaign.gallery_images)
            images = [...images, ...campaignData.campaign.gallery_images];

        return arrayOfUniqueStrings(images);
    }

    const campaignShareURL = useMemo(() => {
        if (campaignData) {
            let share_url = campaignData?.share_url;
            if (store.getState().auth.user?.referral_code) {
                share_url += '/?referrer=' + store.getState().auth.user?.referral_code;
            }
            return share_url;
        }
        return '';
    }, [campaignData])

    const shareURL = async () => {
        if (campaignData) {
            try {
                let share_url = campaignData?.share_url;
                if (store.getState().auth.user?.referral_code) {
                    share_url += '/?referrer=' + store.getState().auth.user?.referral_code;
                }
                await navigator.clipboard.writeText(share_url);
                toast.success('URL copied to your clipboard! 🙂');
            } catch (error) {
                console.error('Failed to copy text');
            }
        }
    }

    /* Helper functions */

    /* Lifecycle hooks */

    useEffect(() => {
        if (campaignData) {
            dispatch(setHeader({
                showTicker: true,
                generic: false,
                page: {
                    title: campaignData?.campaign?.name ?? 'Campaign',
                    onBackClick: () => {
                        navigate('/campaign');
                    },
                    rightContent: (<div className="d-flex align-items-center gap-2 justify-content-end">
                        <button onClick={async () => {
                            if (navigator.share) {
                                try {
                                    await navigator.share({ title: campaignData?.campaign?.name, url: campaignShareURL })
                                } catch (error) {
                                    shareURL();
                                }
                            } else {
                                shareURL();
                            }
                        }} className="unset-all cursor-pointer">
                            <ShareIcon />
                        </button>
                    </div>)
                }
            }))
        }

        return () => {
            dispatch(setHeader(defaultAppHeaderProps))
        }
    }, [campaignData])

    useEffect(() => {
        if (applicationData) {
            setCanApply(applicationData.can_apply ?? false);
            if (applicationData.application)
                setApplicationStatus({ status: 'APPLIED' });
        }
    }, [applicationData])

    useEffect(() => {
        setLocationRequired(applicationData?.require_location ?? false);
    }, [applicationData])

    useEffect(() => {
        fetchCampaignData();
    }, [campaignId, fetchCampaignData])

    useEffect(() => {
        if (profileUpdateModalVisibility && influencer?.name && influencer?.age && influencer.gender && influencer?.location)
            dispatch(toggleProfileUpdateModal(false));
    }, [influencer, profileUpdateModalVisibility])

    useEffect(() => {
        if (locationUpdateModalVisibility && influencer?.location)
            dispatch(toggleLocationUpdateModal(false));
    }, [influencer, locationUpdateModalVisibility])

    useEffect(() => {
        if (addSocialAccountModalVisibility?.active && socialAccount)
            showConfirmationBox();
    }, [socialAccount, addSocialAccountModalVisibility])

    useEffect(() => {
        dispatch(setShowFooterNav(false));
        return () => {
            dispatch(setShowFooterNav(true));
        }
    }, [dispatch])

    useEffect(() => {
        if (campaignData) {
            const platformKey = campaignData.campaign.platform;
            const platform = platforms[platformKey as keyof typeof platforms];
            setCampaignPlatform(platform ?? null);
        }
    }, [campaignData])

    useEffect(() => {
        if (campaignPlatform) {
            const eligibleAccounts = socialAccounts?.filter(account => account.social_account.platform === campaignPlatform.id);
            setEligibleSocialAccounts(eligibleAccounts ?? []);
        }
    }, [socialAccounts, campaignPlatform])

    useEffect(() => {
        if (eligibleSocialAccounts.length === 1) {
            setSocialAccount(eligibleSocialAccounts[0]);
        }
    }, [eligibleSocialAccounts])

    useEffect(() => {
        setSelectedSocialPostId(null);
    }, [socialAccount?.social_account?.uuid])

    useEffect(() => {
        if (campaignAccountSelectorVisible && socialAccount) {
            setCampaignAccountSelectorVisible(false);
            showConfirmationBox();
        }
    }, [campaignAccountSelectorVisible, socialAccount])

    useEffect(() => {
        if (campaignData?.campaign) {
            analyticsLog('campaign_view', {
                campaign_id: campaignData?.campaign.uuid,
                campaign_name: campaignData?.campaign.name,
                brand_name: campaignData?.campaign?.brand?.name
            })
        }
    }, [campaignData])

    useEffect(() => {
        if (socialAccounts.length > 0 && profileCompletion === 100) {
            showNotificationEnablePrompt();
        }
    }, [socialAccounts, profileCompletion])

    /* Lifecycle hooks */

    const specs = [
        {
            label: "Milestone",
            value: campaignData?.milestones?.[0]?.gig_type?.name,
        },
        toValidNumber(campaignData?.campaign?.barter_budget) > 0 && {
            label: "Reimbursement",
            value: campaignData?.campaign?.barter_reimbursement ? "Yes" : "No",
        },
        toValidNumber(campaignData?.campaign?.budget) > 0 && {
            label: "Earn Upto",
            value: `₹${campaignData?.campaign?.budget}`,
            textColor: 'text-success',
        },
        toValidNumber(campaignData?.campaign?.barter_budget) > 0 && {
            label: campaignData?.campaign?.barter_reimbursement ? "Reimbursement" : "Barter Value",
            value: `₹${campaignData?.campaign?.barter_budget}`,
            textColor: 'text-success',
        },
    ].filter(Boolean);

    const requirements = [
        campaignData?.campaign?.requirements?.follower_count?.min && {
            label: "Followers",
            value: `${campaignData?.campaign.requirements.follower_count.min}+`,
        },
        campaignData?.campaign?.requirements?.gender !== 'A' && {
            label: "Creator's Gender",
            value: campaignData?.campaign?.requirements?.gender_name,
        },

        campaignData?.campaign?.requirements?.age?.min && {
            label: "Creator's Age",
            value: `${campaignData.campaign?.requirements?.age?.min} - ${campaignData.campaign?.requirements?.age?.max}`,
        },

        (campaignData?.campaign?.requirements?.regions ?? [])?.length > 0 && {
            label: "Creator Region(s)",
            values: campaignData?.campaign?.requirements?.regions,
            isList: true,
        },

        (campaignData?.campaign?.requirements?.cities ?? [])?.length > 0 &&
        (!campaignData?.campaign?.requirements?.regions?.length) && {
            label: "Creator Location(s)",
            values: campaignData?.campaign?.requirements?.cities,
            isList: true,
        },

        campaignData?.campaign?.requirements?.audience_gender !== 'A' && {
            label: "Audience Gender",
            value: campaignData?.campaign.requirements?.audience_gender_name,
        },

        (campaignData?.campaign?.requirements?.audience_cities ?? [])?.length > 0 && {
            label: "Audience Location(s)",
            values: campaignData?.campaign?.requirements?.audience_cities,
            isList: true,
        },

        (campaignData?.campaign?.requirements?.audience_age_groups ?? [])?.length > 0 && {
            label: "Audience Age",
            values: campaignData?.campaign?.requirements?.audience_age_groups,
            isList: true,
        },

        (campaignData?.campaign?.requirements?.languages ?? [])?.length > 0 && {
            label: "Languages",
            values: campaignData?.campaign?.requirements?.languages?.map(
                (id) => languages[id]
            ),
            isList: true,
        },
    ].filter(Boolean);

    const renderSpec = (item: any) => (
        <div className="d-flex flex-column gap-1">
            {
                item.isMultiLine ? (
                    <div className="d-flex flex-column gap-0">
                        {
                            item.values.map((v: string, i: number) => (
                                <span key={i} className={`${item.textColor ?? 'text-dark'} fs-14 fw-500`}>{v}</span>
                            ))
                        }
                    </div>
                ) : (
                    item.isList ? (
                        <span className={`${item.textColor ?? 'text-dark'} fs-14 fw-500`}>{item.values.slice(0, 2).join(', ')}{item.values.length > 2 ? <span className="text-primary cursor-pointer" onClick={() => setShowMore({ label: item.label, values: item.values })}> +{item.values.length - 2}</span> : ''}</span>
                    ) : (
                        <span className={`${item.textColor ?? 'text-dark'} fs-14 fw-500`}>{item.value}</span>
                    )
                )
            }
            <span className="text-muted fs-12 fw-400">{item.label}</span>
        </div>
    );

    const getCampaignRequirements = () => {
        const rows = [];

        for (let i = 0; i < requirements.length; i += 2) {
            rows.push(
                <>
                    <CampaignSpecsRow
                        key={i}
                        col1={renderSpec(requirements[i])}
                        col2={requirements[i + 1] ? renderSpec(requirements[i + 1]) : <div />}
                    />
                    {i < requirements.length - 2 && <div className="divider m-0"></div>}
                </>
            );
        }
        return rows;
    }

    const getCampaignSpecs = () => {
        const rows = [];
        for (let i = 0; i < specs.length; i += 2) {
            rows.push(
                <>
                    <CampaignSpecsRow
                        key={i}
                        col1={renderSpec(specs[i])}
                        col2={specs[i + 1] ? renderSpec(specs[i + 1]) : <div />}
                    />
                    {i < specs.length - 2 && <div className="divider m-0"></div>}
                </>
            );
        }

        return rows;
    }

    const invalidAnswerError = applicationError?.error_code === ERROR.INVALID_ANSWER;

    /* Renders */
    if (!campaignData) return <CampaignDetailSkeleton />

    return (
        <React.Fragment>
            <div className="app-page with-ticker mb-4">
                <div className="position-relative">
                    <CampaignImageSlider images={getCampaignImages()} />
                    <div className="sponsorship-card-platforms campaign-page">
                        {campaignData?.campaign?.platform === "ig" && (
                            <span className="sponsorship-platform-icon">
                                <img src={instagramLogo as unknown as string} alt="Instagram" className="sponsorship-platform-logo" />
                            </span>
                        )}
                        {campaignData?.campaign?.platform === "yt" && (
                            <span className="sponsorship-platform-icon">
                                <img src={youtubeLogo as unknown as string} alt="YouTube" className="sponsorship-platform-logo" />
                            </span>
                        )}
                    </div>
                </div>
                <div className="app-card my-2 py-18 px-3 d-flex flex-column gap-3">
                    <div>
                        <div className="d-flex align-items-center justify-content-between gap-2">
                            <div className="d-flex flex-column gap-2">
                                <span className="fs-18 fw-700 text-dark">{campaignData?.campaign.name}</span>
                                <span className="fs-16 text-muted fw-400">{campaignData?.brand?.category}</span>
                            </div>
                        </div>
                        <div className='d-flex flex-wrap gap-1 mt-2'>
                            {
                                (campaignData?.campaign?.speciality_niche_names ?? []).length > 0 ? (
                                    <>
                                        {
                                            campaignData.campaign.speciality_niche_names?.map((niche, index) => {
                                                return (
                                                    <span key={index} className="app-chip fs-12 fw-400 text-dark-gray">
                                                        {niche}
                                                    </span>
                                                );
                                            })
                                        }
                                    </>
                                ) : (
                                    <>
                                        {
                                            campaignData.campaign.niche_names?.map((niche, index) => {
                                                return (
                                                    <span key={index} className="app-chip">
                                                        {niche}
                                                    </span>
                                                );
                                            })
                                        }
                                    </>
                                )
                            }
                        </div>
                    </div>
                    <GetProSubscriptionCard applicationData={applicationData} />
                    <div className='app-card-wrapper box-shadow-none position-relative overflow-hidden'>
                        <div className="campaign-specs-card">
                            {getCampaignSpecs()}
                        </div>
                        {
                            toValidNumber(campaignData?.campaign?.barter_budget) > 0 &&
                            <div className="px-075 py-2">
                                <span className="fs-12 fw-400 text-dark">
                                    {
                                        campaignData?.campaign?.barter_reimbursement ? "You will have to buy product and it will be reimbursed by the brand." : "Brand will provide product for free."
                                    }
                                </span>

                            </div>
                        }
                    </div>
                    <div className="campaign-specs-card">
                        {getCampaignRequirements()}
                    </div>
                </div>
                <div className="app-card my-3 py-18 px-3 d-flex flex-column gap-075">
                    <h3 className="m-0 fs-16 fw-700 text-dark">Media Requirements</h3>
                    {
                        [...(campaignData?.milestones ?? [])].sort((a, b) => a.sort_order - b.sort_order).map((milestone, i) => (
                            <div className='specification-card-content' key={i}>
                                {
                                    milestone.gig_type.slug !== 'general' &&
                                    <h6 className="m-0 text-dark-gray fs-14 fw-400">{(campaignData.milestones ?? []).length > 1 ? `#${milestone.sort_order} ` : ''}{milestone.gig_type.name}</h6>
                                }
                                <div className="milestone-media-requirements">
                                    {
                                        campaignData?.campaign.platform === 'yt' &&
                                        <div className="milestone-media-requirement">
                                            <span>{milestone.content_media_types.VIDEO}</span>
                                            <span>Video</span>
                                        </div>
                                    }
                                    {
                                        campaignData?.campaign.platform === 'ig' &&
                                        <>
                                            <div className="milestone-media-requirement">
                                                <span>{milestone.content_media_types.REELS}</span>
                                                <span>Reel</span>
                                            </div>
                                            <div className="milestone-media-requirement">
                                                <span>{milestone.content_media_types.STORY}</span>
                                                <span>Story</span>
                                            </div>
                                            <div className="milestone-media-requirement">
                                                <span>{milestone.content_media_types.FEED}</span>
                                                <span>Post</span>
                                            </div>
                                        </>
                                    }
                                </div>
                            </div>
                        ))
                    }
                </div>
                <div className="app-card my-3 py-18 px-3 d-flex flex-column gap-075">
                    <h3 className="m-0 fs-16 fw-700 text-dark">Campaign Terms</h3>
                    {
                        [...(campaignData?.milestones ?? [])].sort((a, b) => a.sort_order - b.sort_order).map((milestone, i) => (
                            <div className='specification-card-content' key={i}>
                                <div className="milestone-media-requirements">
                                    {
                                        milestone.payment_days &&
                                        <div className="milestone-media-requirement">
                                            <span>{milestone.payment_days} <span className="subtext">day{milestone.payment_days > 1 ? 's' : ''}</span></span>
                                            <span>Payment</span>
                                        </div>
                                    }
                                    {
                                        milestone?.specifications?.content_rights_duration &&
                                        <div className="milestone-media-requirement">
                                            <span>{milestone.specifications.content_rights_duration} <span className='subtext'>month{Number(milestone.specifications.content_rights_duration) > 1 ? 's' : ''}</span></span>
                                            <span>Content Rights</span>
                                        </div>
                                    }
                                    {
                                        milestone?.specifications?.ad_rights_duration &&
                                        <div className="milestone-media-requirement">
                                            <span>{milestone.specifications.ad_rights_duration} <span className='subtext'>month{Number(milestone.specifications.ad_rights_duration) > 1 ? 's' : ''}</span></span>
                                            <span>Ad Rights</span>
                                        </div>
                                    }
                                </div>
                            </div>
                        ))
                    }
                </div>
                <div className="app-card my-3 py-18 px-3 d-flex flex-column gap-075">
                    <h3 className="m-0 fs-16 fw-700 text-dark">About Brand</h3>
                    <div>
                        {
                            campaignData.brand.website_url &&
                            <a className="" href={campaignData.brand.website_url} target="_blank" rel="noopener noreferrer">{campaignData.brand.website_url.split('?')[0]}</a>
                        }
                        <p className="text-dark-gray" style={{ whiteSpace: 'pre-wrap' }}>{campaignData.brand.description}</p>
                    </div>
                </div>
            </div>

            <div className="sticky-footer flex-column">
                {
                    ((applicationStatus.status !== 'APPLIED' && campaignData?.campaign.status === 'completed') || (!canApply && !applicationData?.application)) &&
                    <button className="w-100 btn btn-primary" disabled={true}>Applications Closed</button>
                }
                {
                    canApply && applicationStatus.status !== 'APPLIED' && (!applicationError) && campaignData?.campaign.status !== 'completed' &&
                    // <button className="w-100 btn btn-primary w-100" disabled={applicationStatus.status === 'PENDING' || ((locationDeclined || !isLocationServiceEnabled) && locationRequired && false)} onClick={showConfirmationBox}>{applicationStatus.status === 'PENDING' ? 'Applying..' : 'Apply'}</button>
                    <button className="w-100 btn btn-primary w-100" disabled={applicationStatus.status === 'PENDING'} onClick={showConfirmationBox}>{applicationStatus.status === 'PENDING' ? 'Applying..' : 'Apply'}</button>
                }
                {
                    applicationStatus.status === 'APPLIED' && (!applicationError) &&
                    <Link to={{ pathname: ((applicationData?.application ?? '') as string) === 'accepted' ? '/collaborations' : "/applications" }} className="w-100 btn btn-primary w-100">View Status</Link>
                }
            </div>
            <InfluencerActionCardModal
                isActive={connectionRequiredModalVisibility ?? true}
                onClose={() => {
                    setConnectionRequiredModalVisibility(false);
                    if (eligibleSocialAccounts.length > 1) {
                        setSocialAccount(null);
                    }
                }}
                socialAccount={socialAccount?.social_account}
            />
            <CampaignAccountSelectorModal
                isActive={campaignAccountSelectorVisible}
                onClose={() => setCampaignAccountSelectorVisible(false)}
                socialAccounts={eligibleSocialAccounts}
                campaignInvitation={campaignInvitation}
                onAccountSelection={(account) => {
                    setSocialAccount(account);
                }}
            />
            <ShowMoreContentModal
                isActive={showMore !== null}
                onClose={() => setShowMore(null)}
                showMore={showMore}
            />
            {
                canApply && socialAccount &&
                <CampaignSocialPostSelectionModal
                    isActive={socialPostSelectorVisible}
                    onClose={() => {
                        setSocialPostSelectorVisible(false);
                        setSelectedSocialPostId(null);
                        if (eligibleSocialAccounts.length > 1) {
                            setSocialAccount(null);
                        }
                    }}
                    socialAccount={socialAccount}
                    onContinue={(socialPostId) => {
                        setSelectedSocialPostId(socialPostId);
                        setSocialPostSelectorVisible(false);
                        openApplyConfirmation();
                    }}
                />
            }
            {
                canApply &&
                <React.Fragment>
                    <CampaignApplyConfirmationModal
                        isActive={confirmationBox || invalidAnswerError ? true : false}
                        onClose={() => {
                            setConfirmationBox(false)
                            setSelectedSocialPostId(null);
                            if (eligibleSocialAccounts.length > 1) {
                                setSocialAccount(null);
                            }
                            if (invalidAnswerError) {
                                setApplicationError(null);
                            }
                        }}
                        applicationError={applicationError}
                        application={applicationData!}
                        apply={apply}
                        socialAccount={socialAccount?.social_account!}
                        status={applicationStatus.status}
                        campaignRequirements={campaignData.campaign.requirements}
                        negotiable={campaignData?.campaign?.fixed_offer === 'n' && negotiationMilestone !== null}
                        onRequestHigherPayout={(languageId, answers) => {
                            setPendingApplication({ languageId, answers });
                            setApplicationError(null);
                            setConfirmationBox(false);
                            setNegotiationVisible(true);
                        }}
                    />
                    {
                        negotiationMilestone &&
                        <CampaignApplicationNegotiation
                            isActive={negotiationVisible}
                            onClose={() => {
                                setNegotiationVisible(false);
                                setPendingApplication(null);
                                setSelectedSocialPostId(null);
                                if (eligibleSocialAccounts.length > 1) {
                                    setSocialAccount(null);
                                }
                            }}
                            apply={apply}
                            socialAccount={socialAccount?.social_account!}
                            status={applicationStatus.status}
                            gigAmount={toValidNumber(campaignData?.campaign?.budget)}
                            languageId={pendingApplication?.languageId}
                            answers={pendingApplication?.answers}
                        />
                    }
                    <OutOfCredits
                        isActive={(applicationError && applicationError.error_code === ERROR.MONTHLY_APPLICATIONS_EXHAUSTED) ? true : false}
                        onClose={() => setApplicationError(null)}
                    />
                    <UpgradeToProModal origin="campaign-application"
                        isActive={(applicationError && applicationError.error_code === ERROR.PRO_REQUIRED) ? true : false}
                        message={applicationError?.message}
                        onClose={() => setApplicationError(null)}
                    />
                    <InsufficientPermissionsCTAModal
                        isActive={(applicationError && applicationError.error_code === ERROR.INSUFFICIENT_PERMISSIONS) ? true : false}
                        onClose={() => setApplicationError(null)}
                        missing_permissions={applicationError?.missing_permissions!}
                        feature={applicationError?.feature!}
                        page="campaign"
                    />
                    <GenericErrorsCTAModal
                        isActive={(applicationError && ![ERROR.INSUFFICIENT_PERMISSIONS, ERROR.MONTHLY_APPLICATIONS_EXHAUSTED, ERROR.PRO_REQUIRED, ERROR.INVALID_ANSWER].includes(applicationError.error_code)) ? true : false}
                        onClose={() => setApplicationError(null)}
                        error={applicationError!}
                    />
                    <ProFeaturesModal
                        isActive={proFeaturesModalVisibility}
                        onClose={() => {
                            dispatch(setProFeaturesModalVisibility(false));
                        }}
                    />
                </React.Fragment>

            }
        </React.Fragment>

    );
}

export default Campaign;